/**
 * Platform Analytics Page - Admin
 * Cross-tenant analytics and platform metrics
 */

import {
  BarChart3,
  TrendingUp,
  Activity,
  Zap,
  Globe,
  Users,
  Clock,
  Loader2
} from 'lucide-react';
import Card from '../../components/common/Card';
import { useAllTenants } from '../../hooks/useTenant';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../../services/api';

export default function PlatformAnalytics() {
  const { data: tenants = [], isLoading: tenantsLoading } = useAllTenants();

  // Fetch platform analytics from API
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['admin-platform-analytics'],
    queryFn: async () => {
      const response = await api.get('/v1/admin/analytics');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Use API data with zero defaults
  const apiCallsData = analyticsData?.apiCallsByTenant || [];
  const recommendationsData = analyticsData?.recommendationsOverTime || [];
  const totalApiCalls = analyticsData?.totalApiCalls || 0;
  const totalRecommendations = analyticsData?.totalRecommendations || 0;
  const avgResponseTime = analyticsData?.avgResponseTime || 0;
  const successRate = analyticsData?.successRate || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Platform Analytics</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Cross-tenant performance metrics and insights
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total API Calls</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">{(totalApiCalls / 1000).toFixed(0)}K</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Recommendations</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">{(totalRecommendations / 1000).toFixed(0)}K</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Response Time</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">{avgResponseTime}ms</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
              <Activity className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Success Rate</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">{successRate}%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Calls by Tenant */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">API Calls by Tenant</h3>
          <div className="h-64">
            {apiCallsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={apiCallsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Area type="monotone" dataKey="ourbus" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} name="OurBus" />
                  <Area type="monotone" dataKey="redbus" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="RedBus" />
                  <Area type="monotone" dataKey="vrl" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="VRL" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                No API call data available
              </div>
            )}
          </div>
        </Card>

        {/* Recommendation Acceptance */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Recommendation Acceptance</h3>
          <div className="h-64">
            {recommendationsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recommendationsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="count" fill="#3B82F6" name="Total" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="accepted" fill="#10B981" name="Accepted" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                No recommendation data available
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Tenant Performance Table */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Tenant Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500">Tenant</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500">API Calls</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500">Recommendations</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500">Acceptance Rate</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500">Revenue Impact</th>
              </tr>
            </thead>
            <tbody>
              {tenantsLoading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : tenants.length > 0 ? (
                tenants.map((tenant) => (
                  <tr key={tenant.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 font-medium text-gray-800 dark:text-white">{tenant.name || tenant.id}</td>
                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">{tenant.apiCalls?.toLocaleString() || '0'}</td>
                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">{tenant.recommendations?.toLocaleString() || '0'}</td>
                    <td className="py-3 px-4 text-right text-green-600">{tenant.acceptanceRate || '0'}%</td>
                    <td className="py-3 px-4 text-right text-green-600 font-medium">{tenant.revenueImpact || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400 dark:text-gray-500">
                    No tenant data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

